import { bottomLevel } from "building-generation/elements/level/levelTools";
import type { ResolvedDatumTable } from "building-types/datums/datumsTypes";
import type {
  FloorAssemblyOverride,
  FloorBoundaryFields,
  GeneratedFloorBoundaries,
  SolidFloorAssemblyOverride,
  SolidFramingFloorAssemblyOverride,
} from "building-types/floors/floorAssemblyTypes";
import { resolveBayFramingField } from "building-types/floors/floorBayResolution";
import { isSplitLineEdge, parseSplitLineEdgeId } from "building-types/floors/floorBayTypes";
import { getBayBoundariesForFloor } from "building-types/floors/floorBayUtils";
import { RimBoardAlignment, SpanDirection } from "building-types/floors/floorComponentsTypes";
import type { FloorSplitData } from "building-types/floors/floorSplitDataTypes";
import {
  DEFAULT_SPLIT_LINE_THICKNESS,
  SplitLineJoistAlignment,
  SplitLineSupportFramingCondition,
  type FloorBay,
} from "building-types/floors/floorSplitDataTypes";
import { floorSystemOpeningFramingConfigToInputGroup } from "building-types/floors/floorSystemOpeningFraming";
import type { FloorSystemOpeningFramingConfig } from "building-types/floors/floorSystemOpeningFramingTypes";
import type { FrameFloorAssemblyEntity } from "building-types/floors/frameFloorAssemblyTypes";
import type { SheathingComponentEntity, SheathingType } from "building-types/floors/sheathingComponentTypes";
import type { SlabComponentEntity } from "building-types/floors/slabComponentTypes";
import type { SlabFloorAssemblyEntity } from "building-types/floors/slabFloorAssemblyTypes";
import { FloorFramingComponentType } from "building-types/framing/floorFramingComponentTypes";
import type { ConstraintLine } from "building-types/framing/floorFramingConstraintTypes";
import type { FloorFramingInput, Framing as FloorFraming } from "building-types/framing/floorFramingTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { EdgeMetadata, EdgeMetadataTable } from "building-types/framing/framingMetaDataTypes";
import type { FramingProfileDescriptor } from "building-types/framing/framingProfileDescriptorTypes";
import { FramingProfileSectionKind } from "building-types/framing/framingProfileDescriptorTypes";
import { LumberProfileCatalog, type LumberProfileType } from "building-types/framing/lumberTypes";
import type { ForeignEntityId } from "building-types/handles/foreignEntityId";
import type {
  GeneratedFramedFloorAssembly,
  GeneratedSlabFloorAssembly,
} from "building-types/horizontalAssemblies/generatedFloorAssemblyTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { Table } from "common/data/table";
import { worldInchesNew } from "common/utils/nominalNumbers";
import { isVoidFloorAssemblyOverride } from "doc/elements/floor/floorAssembly";
import { buildingEntityBaseNew } from "doc/utils/buildingEntityTools";
import { getFramingProfileDescriptor, getFramingProfileDescriptorById, lumberRailNew } from "doc/utils/lumberTools";
import { PolyLine2Containment, classifyContainmentPolyLine2PolyLine2Fast } from "geom/containment";
import { offsetPolyLine2VariableStep } from "geom/offset";
import { planeNewXY, polyLine2OnPlane } from "geom/plane";
import {
  polyLine2Close,
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2Length,
  polyLine2Move,
  polyLine2SetCCW,
  polyLine2SetCW,
} from "geom/polyLine2";
import { closedPolyLine2New } from "geom/polyLine2BrandedTypes";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine3EdgeTangent } from "geom/polyLine3";
import type { Vec2 } from "geom/vec2";
import { getEdgeGlobalOffset, getEdgeIdsForOpening, getEdgeIdsForPerimeter } from "../elements/floor/floor";
import {
  applyOpeningEdgeOverrides,
  floorPerimeterInfoToUse,
  getFloorTransitionEdgeSourceId,
  isFloorTransitionEdge,
} from "../elements/floor/floorMirrorUtils";
import { makeSolidFloorPlate } from "../elements/floor/floorSolid";
import { resolveFreeformFloorTopWorldZ } from "../elements/floors/resolveFloorTopZ";
import { FloorFramingGenerator } from "../elements/structural-framing/floor/floorFraming";
import { computePerEdgeSubfloorOutwardExpansions } from "../elements/structural-framing/floor/floorRimAlignmentTools";
import { entityHandleForSelectingReportingUnitFromEntityId } from "../generationTools/getInstanceIdFromLevel";

export interface FrameFloorAssemblyProperties {
  joistSpacing: number;
  subfloorThickness: number;
  spanDirection: SpanDirection;
  joistProfileDescriptor: FramingProfileDescriptor;
  rimProfileDescriptor: FramingProfileDescriptor;
  subfloorName?: string;
  subfloorType?: SheathingType;
}

export interface SlabFloorAssemblyProperties {
  structuralThickness: number;
  subfloorThickness: number;
}

type JoistDepthByBayId = Table<number>;

export interface ProcessedFloorIsland {
  readonly perimeter: PolyLine2;
  readonly openings: PolyLine2[];
  /** Edge IDs for this perimeter (from boundaries.edgeIds, sliced for this perimeter). */
  readonly perimeterEdgeIds: string[];
  /** Cumulative edge count before this perimeter, for looking up 1-based global keys in floorEdgeOverrides. */
  readonly perimeterGlobalEdgeOffset: number;
  /** Original indices of the openings in the boundaries.openings array. */
  readonly openingOriginalIndices: number[];
}

export interface ProcessedFloorGeometry {
  topDatumZ: number;
  levelId: string;
  /** Structured per-island data preserving edge identity for framing metadata. */
  islands: ProcessedFloorIsland[];
  /** Opening edge IDs after offset (may include transition edges). */
  openingEdgeIds: string[];
  /** Opening edge counts after offset (may differ from original when transition edges are inserted). */
  openingEdgeCounts: number[];
}

const EXTERIOR_RIM_BOARD_ALIGNMENT = RimBoardAlignment.EXTERIOR;

/** Derive the `[perimeter, ...openings][]` format needed by `makeSolidFloorPlate`. */
export function islandsToPerimetersWithHoles(islands: ProcessedFloorIsland[]): PolyLine2[][] {
  return islands.map((island) => [island.perimeter, ...island.openings]);
}

export function shouldProcessFloorWithBoundaries(
  floor: FloorAssemblyOverride | undefined,
  floorSystemId: string,
  resolvedFloorSystems: Table<FloorAssemblyOverride>,
  floorBoundaries: Table<GeneratedFloorBoundaries>,
): boolean {
  if (!floor || floor.disabled) {
    return false;
  }
  if (resolvedFloorSystems[floorSystemId]?.disabled) {
    return false;
  }
  if (isVoidFloorAssemblyOverride(floor)) {
    return false;
  }
  if (floor.parentId !== floorSystemId) {
    return false;
  }
  const boundaries = floorBoundaries[floor.id];
  if (!boundaries || boundaries.perimeters.length === 0) {
    return false;
  }
  return true;
}

export function extractFrameFloorAssemblyProperties(
  frameFloorAssembly: FrameFloorAssemblyEntity | undefined,
  framingComponents: Table<FramingComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
): FrameFloorAssemblyProperties | null {
  if (!frameFloorAssembly) {
    return null;
  }
  const { joistComponentId, rimComponentId, subFloorComponentId } = frameFloorAssembly;
  const joistSpacing = frameFloorAssembly.joistSpacing;
  const spanDirection = frameFloorAssembly.spanDirection;
  const framingComponent = framingComponents[joistComponentId];

  if (!framingComponent) {
    return null;
  }

  const sheathingComponent = sheathingComponents[subFloorComponentId];

  const joistProfileDescriptor = getFramingProfileDescriptor(framingComponent);

  const rimComponent = rimComponentId !== undefined ? framingComponents[rimComponentId] : undefined;
  const rimProfileDescriptor: FramingProfileDescriptor = rimComponent
    ? getFramingProfileDescriptor(rimComponent)
    : joistProfileDescriptor;

  const subfloorThickness = sheathingComponent?.thickness ?? 0;

  return {
    joistSpacing,
    subfloorThickness,
    spanDirection,
    joistProfileDescriptor,
    rimProfileDescriptor,
    subfloorName: sheathingComponent?.name,
    subfloorType: sheathingComponent?.sheathingType,
  };
}

export function extractSlabFloorAssemblyProperties(
  slabFloorAssembly: SlabFloorAssemblyEntity | undefined,
  slabComponents: Table<SlabComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
): SlabFloorAssemblyProperties | null {
  if (!slabFloorAssembly) {
    return null;
  }

  const slabComponent = slabComponents[slabFloorAssembly.slabComponentId];
  if (!slabComponent) {
    return null;
  }

  const sheathingComponent = sheathingComponents[slabFloorAssembly.subFloorComponentId];
  const structuralThickness = slabComponent.thickness;
  const subfloorThickness = sheathingComponent?.thickness ?? 0;

  return {
    structuralThickness,
    subfloorThickness,
  };
}

/**
 * Build ProcessedFloorGeometry from building.floorBoundaries (already mirrored).
 * Assigns each opening to the first perimeter that contains it.
 */
export function processFloorBoundariesToGeometry(
  boundaries: GeneratedFloorBoundaries,
  floor: SolidFloorAssemblyOverride,
  resolvedDatums: ResolvedDatumTable,
  levels: Table<Level>,
): ProcessedFloorGeometry {
  const firstLevel = bottomLevel(levels);
  if (!firstLevel) {
    throw new Error("First level not found");
  }
  if (boundaries.perimeters.length === 0) {
    throw new Error("Floor boundaries must have at least one perimeter");
  }

  const levelToUse = "levelId" in floor && floor.levelId && levels[floor.levelId] ? levels[floor.levelId] : firstLevel;
  const topDatumZ = worldInchesNew(
    resolveFreeformFloorTopWorldZ(levelToUse, resolvedDatums, floor.topDatum, floor.topDatumOffset ?? 0),
  );

  const openingResult = applyOpeningEdgeOverrides(
    boundaries.openings,
    boundaries.openingEdgeCounts,
    boundaries.openingEdgeIds,
    boundaries.floorOpeningEdgeOverrides,
  );
  const openings = openingResult.openings;
  const islands: ProcessedFloorIsland[] = [];

  const combinedEdgeIds = boundaries.edgeIds;
  const perimeterEdgeCounts = boundaries.perimeterEdgeCounts;

  for (let perimeterIndex = 0; perimeterIndex < boundaries.perimeters.length; perimeterIndex++) {
    const perimeter = boundaries.perimeters[perimeterIndex];
    const edgeIdsForPerimeter = getEdgeIdsForPerimeter(combinedEdgeIds, perimeterEdgeCounts, perimeterIndex);
    const globalEdgeOffset = getEdgeGlobalOffset(perimeterEdgeCounts, perimeterIndex);
    const { perimeter: processedPerimeter, edgeIds: processedEdgeIds } = floorPerimeterInfoToUse(
      perimeter,
      edgeIdsForPerimeter,
      boundaries.floorEdgeOverrides,
      floor.lateralOffset,
    );
    const openingsInThisPerimeter: PolyLine2[] = [];
    const openingOriginalIndices: number[] = [];
    for (let openingIndex = 0; openingIndex < openings.length; openingIndex++) {
      if (
        classifyContainmentPolyLine2PolyLine2Fast(openings[openingIndex], processedPerimeter) ===
        PolyLine2Containment.INSIDE
      ) {
        openingsInThisPerimeter.push(openings[openingIndex]);
        openingOriginalIndices.push(openingIndex);
      }
    }
    islands.push({
      perimeter: processedPerimeter,
      openings: openingsInThisPerimeter,
      perimeterEdgeIds: processedEdgeIds,
      perimeterGlobalEdgeOffset: globalEdgeOffset,
      openingOriginalIndices,
    });
  }

  return {
    topDatumZ,
    levelId: levelToUse.id,
    islands,
    openingEdgeIds: openingResult.openingEdgeIds,
    openingEdgeCounts: openingResult.openingEdgeCounts,
  };
}

/**
 * Build EdgeMetadataTable from FloorEdgeOverrides, keyed by stable edge IDs.
 * For transition edges, inherits the framing config from the source edge.
 * For split-line edges, populates splitLineThickness from the split line's
 * framing profile, falling back to explicit thickness and then the floor joist profile.
 */
export function buildEdgeMetadataFromOverrides(
  overrides: FloorBoundaryFields["floorEdgeOverrides"],
  edgeIds: string[],
  splitData?: FloorSplitData,
  defaultSplitLineFramingProfile?: LumberProfileType,
): EdgeMetadataTable | undefined {
  if (edgeIds.length === 0) {
    return undefined;
  }
  const entries: Record<number, EdgeMetadata> = {};
  let hasEntries = false;
  for (let i = 0; i < edgeIds.length; i++) {
    const edgeId = edgeIds[i];

    if (isSplitLineEdge(edgeId)) {
      const thickness = resolveSplitLineEdgeThickness(edgeId, splitData, defaultSplitLineFramingProfile);
      entries[i] = { splitLineThickness: thickness };
      hasEntries = true;
      continue;
    }

    const lookupId = isFloorTransitionEdge(edgeId) ? getFloorTransitionEdgeSourceId(edgeId) ?? edgeId : edgeId;
    const override = overrides?.[lookupId];
    if (override?.framingConfig) {
      entries[i] = { framingConfig: override.framingConfig };
      hasEntries = true;
    }
  }
  return hasEntries ? (entries as EdgeMetadataTable) : undefined;
}

function resolveSplitLineEdgeThickness(
  edgeId: string,
  splitData: FloorSplitData | undefined,
  defaultFramingProfile: LumberProfileType | undefined,
): number {
  const parsed = parseSplitLineEdgeId(edgeId);
  if (!parsed || !splitData) {
    return resolveSplitLineProfileThickness(defaultFramingProfile);
  }
  const splitLine = splitData.splitLines?.[parsed.splitLineId];
  if (!splitLine) {
    return resolveSplitLineProfileThickness(defaultFramingProfile);
  }
  const splitEdgeId = splitLine.edgeIds?.[parsed.edgeIndex];
  const override = splitEdgeId ? splitLine.edgeOverrides?.[splitEdgeId] : undefined;
  if (override?.framingProfile) {
    return resolveSplitLineProfileThickness(override.framingProfile);
  }
  if (override?.thickness !== undefined) {
    return override.thickness;
  }
  return resolveSplitLineProfileThickness(defaultFramingProfile);
}

function resolveSplitLineProfileThickness(framingProfile: LumberProfileType | undefined): number {
  if (!framingProfile) {
    return DEFAULT_SPLIT_LINE_THICKNESS;
  }
  return LumberProfileCatalog[framingProfile]?.height ?? DEFAULT_SPLIT_LINE_THICKNESS;
}

function defaultSplitLineFramingProfileFromDescriptor(
  descriptor: FramingProfileDescriptor,
): LumberProfileType | undefined {
  if (descriptor.sectionKind === FramingProfileSectionKind.DIMENSIONAL) {
    return descriptor.componentDescriptor.type;
  }
  return undefined;
}

/**
 * Expands a perimeter outward by the total rim joist stack thickness on each edge.
 * Per-edge offset = framingCount * profileHeight, where both count and the per-edge
 * framing component (via `framingComponentId`) can be overridden in EdgeMetadata. The
 * result is the subfloor/sheathing outline that covers the full framing depth.
 *
 * `defaultRimProfile` is the rim component descriptor used when no per-edge override
 * is supplied. Its `height` is the in-plane rim stack thickness.
 */
export function expandPerimeterForSubfloor(
  perimeter: PolyLine2,
  edgeMetadata: EdgeMetadataTable | undefined,
  defaultRimProfile: FramingProfileDescriptor,
  rimBoardAlignment: RimBoardAlignment,
  framingComponents?: Table<FramingComponentEntity>,
): PolyLine2 {
  const edgeCount = polyLine2EdgeCount(perimeter);
  const offsets = computePerEdgeSubfloorOutwardExpansions(
    edgeCount,
    edgeMetadata,
    rimBoardAlignment,
    defaultRimProfile,
    framingComponents,
  );

  if (offsets.every((v) => v === 0)) {
    return perimeter;
  }

  const { pl } = offsetPolyLine2VariableStep(polyLine2Copy(perimeter), offsets, true);
  return pl;
}

/**
 * Convert preserved island data + boundaries into FloorFramingInput[] for the new generator.
 * Each island produces one FloorFramingInput (one perimeter + its openings).
 *
 * Bay splitting is handled upstream in generateFloorSystemBoundary. Split-line edges are
 * identified by their stable edge IDs and translated into EdgeMetadata.splitLineThickness
 * via buildEdgeMetadataFromOverrides, which suppresses rim generation and adjusts subfloor
 * expansion on those edges.
 */
function buildFloorFramingInputs(
  geometry: ProcessedFloorGeometry,
  boundaries: GeneratedFloorBoundaries,
  direction: number | SpanDirection,
  framingStartPt: Vec2,
  joistSpacing: number,
  defaultFramingDescriptor: FramingProfileDescriptor,
  elevation: number,
  openingFramingConfig: FloorSystemOpeningFramingConfig | undefined,
  framingComponents: Table<FramingComponentEntity>,
): FloorFramingInput[] {
  const defaultSplitLineFramingProfile = defaultSplitLineFramingProfileFromDescriptor(defaultFramingDescriptor);
  const openingFramingConfigGroup = floorSystemOpeningFramingConfigToInputGroup(
    openingFramingConfig,
    defaultFramingDescriptor.componentId,
  );
  return geometry.islands.map((island) => {
    const perimeterEdgeMetadata = buildEdgeMetadataFromOverrides(
      boundaries.floorEdgeOverrides,
      island.perimeterEdgeIds,
      boundaries.splitData,
      defaultSplitLineFramingProfile,
    );

    const openingBoundaries = island.openings.map((opening, idx) => {
      const originalIndex = island.openingOriginalIndices[idx];
      const openingEdgeIds = getEdgeIdsForOpening(geometry.openingEdgeIds, geometry.openingEdgeCounts, originalIndex);
      const edgeMetadata = buildEdgeMetadataFromOverrides(boundaries.floorOpeningEdgeOverrides, openingEdgeIds);
      return {
        polyline: closedPolyLine2New(polyLine2SetCW(polyLine2Close(opening))),
        edgeMetadata,
      };
    });

    return {
      perimeter: {
        polyline: closedPolyLine2New(polyLine2SetCCW(polyLine2Close(island.perimeter))),
        edgeMetadata: perimeterEdgeMetadata,
      },
      framingMetadata: {
        direction,
        framingStartPt,
        spacing: joistSpacing,
        defaultFramingDescriptor,
        forceTrimmersAtOpeningEnds: true,
        elevation,
      },
      openings: openingBoundaries.length > 0 ? openingBoundaries : undefined,
      openingFramingConfigGroup,
      constraintLines: normalizeSplitLineConstraints(
        boundaries.splitLineConstraints,
        defaultFramingDescriptor.componentId,
        framingComponents,
      ),
    };
  });
}

function normalizeSplitLineConstraints(
  constraintLines: readonly ConstraintLine[] | undefined,
  defaultFramingComponentId: ForeignEntityId<"FRAMING_COMPONENT"> | undefined,
  framingComponents: Table<FramingComponentEntity>,
): ConstraintLine[] | undefined {
  if (!constraintLines || constraintLines.length === 0) {
    return undefined;
  }

  return constraintLines.map((constraint) => {
    const framingComponentId = constraint.framingComponentId ?? defaultFramingComponentId;
    const componentProfile = framingComponentId
      ? getFramingProfileDescriptorById(framingComponents, framingComponentId)
      : undefined;

    return {
      ...constraint,
      width: componentProfile?.height ?? constraint.width,
      framingComponentId,
    };
  });
}

function constraintLineAppliesToFramingAlgorithm(constraint: ConstraintLine): boolean {
  return !(
    constraint.joistAlignment === SplitLineJoistAlignment.DROPPED &&
    constraint.supportFramingCondition === SplitLineSupportFramingCondition.RUN_OVER
  );
}

export function createFramedFloorAssembly(
  floorId: string,
  floorSystemId: string,
  floor: SolidFramingFloorAssemblyOverride,
  properties: FrameFloorAssemblyProperties,
  geometry: ProcessedFloorGeometry,
  boundaries: GeneratedFloorBoundaries,
  framingComponents: Table<FramingComponentEntity>,
  mirror: boolean = false,
  mirrorTranslationX: number = 0,
  parentFloorId?: string,
  bayId?: FloorBay["id"],
  openingFramingConfig?: FloorSystemOpeningFramingConfig,
  joistDepthByBayId?: JoistDepthByBayId,
): GeneratedFramedFloorAssembly {
  const { joistSpacing, subfloorThickness, spanDirection, joistProfileDescriptor, rimProfileDescriptor } = properties;
  const { topDatumZ, levelId } = geometry;

  if (!joistSpacing || !spanDirection || !joistProfileDescriptor) {
    throw new Error("Missing required properties for framed floor assembly");
  }

  const floorSpanDirection = floor.spanDirection ?? spanDirection;
  const floorFramingAngle = floor.framingAngle;
  const docFramingStartPt: Vec2 = floor.framingStartPt ?? [0, 0];
  const floorFramingStartPt: Vec2 = mirror
    ? [-docFramingStartPt[0] + mirrorTranslationX, docFramingStartPt[1]]
    : docFramingStartPt;
  const mirroredAngle = floorFramingAngle !== undefined && mirror ? -floorFramingAngle : floorFramingAngle;
  const direction: number | SpanDirection =
    floorSpanDirection === SpanDirection.SPECIFY && mirroredAngle !== undefined
      ? (mirroredAngle * Math.PI) / 180
      : floorSpanDirection;
  const subfloorPerimetersWithHoles = geometry.islands.map((island) => {
    const edgeMeta = buildEdgeMetadataFromOverrides(
      boundaries.floorEdgeOverrides,
      island.perimeterEdgeIds,
      boundaries.splitData,
    );
    const expandedPerimeter = expandPerimeterForSubfloor(
      island.perimeter,
      edgeMeta,
      rimProfileDescriptor,
      EXTERIOR_RIM_BOARD_ALIGNMENT,
      framingComponents,
    );
    return [expandedPerimeter, ...island.openings];
  });
  const subFloor = makeSolidFloorPlate({
    floorPlatePerimetersWithHoles: subfloorPerimetersWithHoles,
    floorPlateElevation: topDatumZ,
    plateThickness: subfloorThickness ?? 0,
  });
  const elevation = topDatumZ - subfloorThickness;

  const framingInputs = buildFloorFramingInputs(
    geometry,
    boundaries,
    direction,
    floorFramingStartPt,
    joistSpacing,
    joistProfileDescriptor,
    elevation,
    openingFramingConfig,
    framingComponents,
  );

  const framing: FloorFraming[][] = [];
  let memberIdOffset = 0;
  for (const input of framingInputs) {
    const framingAlgorithmConstraintLines = input.constraintLines?.filter(constraintLineAppliesToFramingAlgorithm);
    const generator = new FloorFramingGenerator(
      {
        ...input,
        constraintLines:
          framingAlgorithmConstraintLines && framingAlgorithmConstraintLines.length > 0
            ? framingAlgorithmConstraintLines
            : undefined,
      },
      framingComponents,
      memberIdOffset,
      rimProfileDescriptor,
      EXTERIOR_RIM_BOARD_ALIGNMENT,
    );
    const result = generator.generate();
    const { beams, nextMemberIdOffset } = buildFloorBeamsFromConstraintLines(
      input.constraintLines,
      result.framing,
      framingComponents,
      elevation,
      joistProfileDescriptor,
      memberIdOffset + result.framing.length,
      bayId,
      withGeneratedJoistDepthForBay(joistDepthByBayId, bayId, result.framing, elevation, joistProfileDescriptor),
    );
    framing.push([...result.framing, ...beams]);
    memberIdOffset = nextMemberIdOffset;
  }

  return {
    ...buildingEntityBaseNew(
      "FRAMED_FLOOR_ASSEMBLY",
      floorId,
      entityHandleForSelectingReportingUnitFromEntityId(floorId, { type: "FLOOR_SYSTEM", id: floorSystemId }),
    ),
    floorSystemId,
    framing,
    subfloor: subFloor,
    levelId,
    parentFloorId: parentFloorId ?? floorId,
    bayId,
    subfloorName: properties.subfloorName,
    subfloorType: properties.subfloorType,
    subfloorThickness: properties.subfloorThickness,
  };
}

function buildFloorBeamsFromConstraintLines(
  constraintLines: readonly ConstraintLine[] | undefined,
  framing: readonly FloorFraming[],
  framingComponents: Table<FramingComponentEntity>,
  defaultTopElevation: number,
  defaultJoistProfileDescriptor: FramingProfileDescriptor,
  memberIdStartOffset: number,
  bayId: FloorBay["id"] | undefined,
  joistDepthByBayId?: JoistDepthByBayId,
): { readonly beams: FloorFraming[]; readonly nextMemberIdOffset: number } {
  if (!constraintLines || constraintLines.length === 0) {
    return { beams: [], nextMemberIdOffset: memberIdStartOffset };
  }

  const beams: FloorFraming[] = [];
  let memberIdOffset = memberIdStartOffset;
  for (const constraint of constraintLines) {
    if (polyLine2Length(constraint.polyline) === 0) {
      continue;
    }
    if (constraint.bayId !== undefined && constraint.bayId !== bayId) {
      continue;
    }

    const profileDescriptor = resolveFloorBeamProfile(constraint, defaultJoistProfileDescriptor, framingComponents);
    const topElevation = resolveFloorBeamTopElevation(
      constraint,
      framing,
      profileDescriptor,
      defaultTopElevation,
      defaultJoistProfileDescriptor,
      joistDepthByBayId,
    );
    const rail2D = polyLine2Copy(constraint.polyline);
    const rail3D = polyLine2OnPlane(planeNewXY(0, 0, topElevation), rail2D);
    const [dx, dy] = polyLine3EdgeTangent(rail3D, 0);
    centerBeamTopCrossSectionOnConstraintLine(rail2D, dx, dy, profileDescriptor.height);
    const centeredRail3D = polyLine2OnPlane(planeNewXY(0, 0, topElevation), rail2D);
    const rail = lumberRailNew(centeredRail3D, dy, -dx, 0);
    const memberId = `member-${memberIdOffset++}`;

    beams.push({
      id: memberId,
      type: FloorFramingComponentType.FLOOR_BEAM,
      profileDescriptor,
      rails: [rail],
    });
  }

  return { beams, nextMemberIdOffset: memberIdOffset };
}

function centerBeamTopCrossSectionOnConstraintLine(
  rail2D: PolyLine2,
  tangentX: number,
  tangentY: number,
  beamThickness: number,
): void {
  // The lumber rail is one top edge of the profile. Move it opposite its
  // in-plane profile normal so the top-face centerline lies on the constraint.
  polyLine2Move(rail2D, -tangentY * beamThickness * 0.5, tangentX * beamThickness * 0.5);
}

function resolveFloorBeamProfile(
  constraint: ConstraintLine,
  defaultJoistProfileDescriptor: FramingProfileDescriptor,
  framingComponents: Table<FramingComponentEntity>,
): FramingProfileDescriptor {
  if (constraint.framingComponentId) {
    const descriptor = getFramingProfileDescriptorById(framingComponents, constraint.framingComponentId);
    if (descriptor) {
      return descriptor;
    }
  }
  return defaultJoistProfileDescriptor;
}

function resolveFloorBeamTopElevation(
  constraint: ConstraintLine,
  framing: readonly FloorFraming[],
  beamProfile: FramingProfileDescriptor,
  defaultTopElevation: number,
  defaultJoistProfileDescriptor: FramingProfileDescriptor,
  joistDepthByBayId?: JoistDepthByBayId,
): number {
  if ((constraint.joistAlignment ?? SplitLineJoistAlignment.FLUSH_TOP) === SplitLineJoistAlignment.FLUSH_TOP) {
    return defaultTopElevation;
  }

  const joistDepth = resolveConstraintAdjacentJoistDepth(
    constraint,
    framing,
    defaultTopElevation,
    defaultJoistProfileDescriptor,
    joistDepthByBayId,
  );
  const joistBottomElevation = defaultTopElevation - joistDepth;

  if (constraint.joistAlignment === SplitLineJoistAlignment.DROPPED) {
    return joistBottomElevation;
  }

  return joistBottomElevation + beamProfile.width;
}

function resolveConstraintAdjacentJoistDepth(
  constraint: ConstraintLine,
  framing: readonly FloorFraming[],
  defaultTopElevation: number,
  defaultJoistProfileDescriptor: FramingProfileDescriptor,
  joistDepthByBayId: JoistDepthByBayId | undefined,
): number {
  const adjacentDepths: number[] = [];
  const leftDepth = constraint.bayLeftId ? joistDepthByBayId?.[constraint.bayLeftId] : undefined;
  const rightDepth = constraint.bayRightId ? joistDepthByBayId?.[constraint.bayRightId] : undefined;

  if (leftDepth !== undefined) {
    adjacentDepths.push(leftDepth);
  }
  if (rightDepth !== undefined) {
    adjacentDepths.push(rightDepth);
  }
  if (adjacentDepths.length > 0) {
    return Math.max(...adjacentDepths);
  }

  return resolveGeneratedJoistDepth(framing, defaultTopElevation, defaultJoistProfileDescriptor);
}

function withGeneratedJoistDepthForBay(
  joistDepthByBayId: JoistDepthByBayId | undefined,
  bayId: FloorBay["id"] | undefined,
  framing: readonly FloorFraming[],
  defaultTopElevation: number,
  defaultJoistProfileDescriptor: FramingProfileDescriptor,
): JoistDepthByBayId | undefined {
  if (!bayId) {
    return joistDepthByBayId;
  }
  return {
    ...joistDepthByBayId,
    [bayId]: resolveGeneratedJoistDepth(framing, defaultTopElevation, defaultJoistProfileDescriptor),
  };
}

function resolveGeneratedJoistDepth(
  framing: readonly FloorFraming[],
  defaultTopElevation: number,
  defaultJoistProfileDescriptor: FramingProfileDescriptor,
): number {
  const lowestJoistBottomElevation = framing.reduce<number | undefined>((lowest, member) => {
    if (member.type === FloorFramingComponentType.FLOOR_BEAM) {
      return lowest;
    }
    const rail = member.rails[0]?.rail;
    const topElevation = rail?.[2];
    if (topElevation === undefined) {
      return lowest;
    }
    const bottomElevation = topElevation - member.profileDescriptor.width;
    return lowest === undefined ? bottomElevation : Math.min(lowest, bottomElevation);
  }, undefined);

  if (lowestJoistBottomElevation === undefined) {
    return defaultJoistProfileDescriptor.width;
  }
  return defaultTopElevation - lowestJoistBottomElevation;
}

export function createSlabFloorAssembly(
  floorId: string,
  floorSystemId: string,
  properties: SlabFloorAssemblyProperties,
  geometry: ProcessedFloorGeometry,
  parentFloorId?: string,
  bayId?: FloorBay["id"],
): GeneratedSlabFloorAssembly {
  const { structuralThickness, subfloorThickness } = properties;
  const { topDatumZ, levelId } = geometry;

  if (!structuralThickness) {
    throw new Error("Missing structural thickness for slab floor assembly");
  }

  const floorPlatePerimetersWithHoles = islandsToPerimetersWithHoles(geometry.islands);
  const subFloor = makeSolidFloorPlate({
    floorPlatePerimetersWithHoles,
    floorPlateElevation: topDatumZ,
    plateThickness: subfloorThickness ?? 0,
  });

  const slab = makeSolidFloorPlate({
    floorPlatePerimetersWithHoles,
    floorPlateElevation: topDatumZ - subfloorThickness,
    plateThickness: structuralThickness,
  });

  return {
    ...buildingEntityBaseNew(
      "SLAB_FLOOR_ASSEMBLY",
      floorId,
      entityHandleForSelectingReportingUnitFromEntityId(floorId, { type: "FLOOR_SYSTEM", id: floorSystemId }),
    ),
    floorSystemId,
    slab: slab,
    subfloor: subFloor,
    levelId: levelId,
    parentFloorId: parentFloorId ?? floorId,
    bayId,
  };
}

export interface BoundaryToProcess {
  readonly boundaryId: string;
  readonly boundaryData: GeneratedFloorBoundaries;
  readonly assemblyFloor: SolidFramingFloorAssemblyOverride;
}

/**
 * Collects the boundaries to process for a floor. Always routes through bay
 * boundaries when they exist in allBoundaries (entries with parentFloorId matching
 * floorId). Bay-level framing overrides are applied from splitData.bays.
 * Falls back to the parent floor boundary only if no bay boundaries are found.
 */
export function collectBoundariesToProcess(
  floorId: string,
  parentBoundary: GeneratedFloorBoundaries,
  allBoundaries: Table<GeneratedFloorBoundaries>,
  splitData: FloorSplitData | undefined,
  effectiveFloor: SolidFramingFloorAssemblyOverride,
): BoundaryToProcess[] {
  const result: BoundaryToProcess[] = [];
  for (const { bayId, boundary } of getBayBoundariesForFloor(allBoundaries, floorId)) {
    const bayDef = splitData?.bays?.[bayId];
    const bayAssemblyFloor = {
      ...effectiveFloor,
      assemblyId: resolveBayFramingField(bayDef, effectiveFloor.assemblyId, "assemblyId") ?? effectiveFloor.assemblyId,
      spanDirection:
        resolveBayFramingField(bayDef, effectiveFloor.spanDirection, "spanDirection") ?? effectiveFloor.spanDirection,
      framingAngle:
        resolveBayFramingField(bayDef, effectiveFloor.framingAngle, "framingAngle") ?? effectiveFloor.framingAngle,
      framingStartPt:
        resolveBayFramingField(bayDef, effectiveFloor.framingStartPt, "framingStartPt") ??
        effectiveFloor.framingStartPt,
    };

    result.push({ boundaryId: bayId, boundaryData: boundary, assemblyFloor: bayAssemblyFloor });
  }

  if (result.length === 0) {
    return [{ boundaryId: floorId, boundaryData: parentBoundary, assemblyFloor: effectiveFloor }];
  }

  return result;
}
